import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Spin,
  Modal,
  Upload,
  Alert,
  Table,
  Tag,
  Checkbox,
  App
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  GlobalOutlined,
  CopyOutlined,
  BuildOutlined,
  UploadOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import businessUnitService from '../services/businessUnitService';
import settingsService from '../services/settingsService';
import PageHeader from './PageHeader';
import SearchCard from './SearchCard';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { Dragger } = Upload;

const { Title } = Typography;

const cardStyles = `
  .business-unit-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    transition: all 0.3s ease;
    cursor: pointer;
  }
  
  .business-unit-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    transform: translateY(-2px);
  }
  
  .edit-button {
    position: absolute;
    top: 12px;
    right: 12px;
    color: #2E86AB;
  }
  
  .member-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
    margin-right: 4px;
  }
`;

const BusinessUnits = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkImportVisible, setBulkImportVisible] = useState(false);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportDryRun, setBulkImportDryRun] = useState(true);
  const [bulkImportWasDryRun, setBulkImportWasDryRun] = useState(false);
  const [bulkImportLastMoveExisting, setBulkImportLastMoveExisting] = useState(true);
  
  // Available options from backend
  const [availableOptions, setAvailableOptions] = useState({
    industries: [],
    countries: [],
    regions: [],
    managingGroups: [],
    departments: []
  });
  
  // Filter states
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');

  // Load business units
  const loadBusinessUnits = async () => {
    setLoading(true);
    try {
      const result = await businessUnitService.getAllBusinessUnits();
      if (result.success) {
        const units = result.data || [];
        setBusinessUnits(units);
        console.log('Loaded business units:', units);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('Load business units error:', error);
      message.error('Failed to load business units');
    } finally {
      setLoading(false);
    }
  };

  // Load available options for filters
  const loadAvailableOptions = async () => {
    try {
      const settingsResult = await settingsService.getSettings();
      if (settingsResult.success) {
        const settings = settingsResult.data.data;
        
        // Extract unique departments from all business units
        const allDepartments = new Set();
        
        // Add departments from all business units
        businessUnits.forEach(unit => {
          if (unit.departments && Array.isArray(unit.departments)) {
            unit.departments.forEach(dept => {
              if (dept && typeof dept === 'string' && dept.trim()) {
                allDepartments.add(dept.trim());
              }
            });
          }
        });

        setAvailableOptions({
          industries: (settings?.industries || []).slice().sort((a, b) => a.localeCompare(b)),
          countries: settings?.countries || [],
          regions: settings?.regions || [],
          managingGroups: settings?.managingGroups || [],
          departments: Array.from(allDepartments).sort()
        });
      }
    } catch (error) {
      console.error('Load available options error:', error);
    }
  };

  useEffect(() => {
    loadBusinessUnits();
  }, []);

  useEffect(() => {
    // Load available options after business units are loaded
    if (businessUnits.length > 0) {
      loadAvailableOptions();
    }
  }, [businessUnits]);

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    switch (filterKey) {
      case 'industry':
        setSelectedIndustry(value || '');
        break;
      case 'country':
        setSelectedCountry(value || '');
        break;
      case 'region':
        setSelectedRegion(value || '');
        break;
      case 'managingGroup':
        setSelectedGroup(value || '');
        break;
      default:
        break;
    }
  };

  // Create filter options object
  const filterOptions = {
    industries: availableOptions.industries,
    countries: availableOptions.countries,
    regions: availableOptions.regions,
    managingGroups: availableOptions.managingGroups,
    departments: availableOptions.departments
  };

  // Helper functions
  const getFilteredBusinessUnits = () => {
    if (!Array.isArray(businessUnits)) return [];
    return businessUnits.filter(unit => {
      const searchLower = searchText && typeof searchText === 'string' ? searchText.toLowerCase() : '';
      const matchesSearch = !searchLower || 
        unit?.name?.toLowerCase().includes(searchLower) ||
        unit?.industry?.toLowerCase().includes(searchLower) ||
        unit?.country?.toLowerCase().includes(searchLower);
      
      const matchesIndustry = !selectedIndustry || unit.industry === selectedIndustry;
      const matchesCountry = !selectedCountry || unit.country === selectedCountry;
      const matchesRegion = !selectedRegion || unit.region === selectedRegion;
      const matchesGroup = !selectedGroup || unit.managingGroup === selectedGroup;
      
      return matchesSearch && matchesIndustry && matchesCountry && matchesRegion && matchesGroup;
    });
  };

  const handleBulkImport = () => {
    if (!bulkImportFile) { message.error('Please select a CSV file first'); return; }

    modal.confirm({
      title: 'Update existing participants?',
      content: 'Some people in this file may already exist (matched by email or phone number). Do you want to move them to the organization/department/group given in this file? If you choose "No", those rows will be skipped instead.',
      okText: 'Yes, update them',
      cancelText: 'No, skip duplicates',
      onOk: () => runBulkImport(true, bulkImportDryRun),
      onCancel: () => runBulkImport(false, bulkImportDryRun),
    });
  };

  const runBulkImport = async (moveExisting, dryRun) => {
    setBulkImportLoading(true);
    setBulkImportResults(null);
    try {
      const fd = new FormData();
      fd.append('csvFile', bulkImportFile);
      fd.append('moveExisting', moveExisting ? 'true' : 'false');
      fd.append('dryRun', dryRun ? 'true' : 'false');
      const result = await businessUnitService.bulkImportOrganizations(fd);
      if (result.success) {
        setBulkImportResults(result.data.data);
        setBulkImportWasDryRun(!!result.data.dryRun);
        setBulkImportLastMoveExisting(moveExisting);
        message.success(result.data.message);
        if (!dryRun) loadBusinessUnits();
      } else {
        message.error(result.message || 'Import failed');
      }
    } catch (e) {
      message.error('Import failed');
    } finally {
      setBulkImportLoading(false);
    }
  };

  // Re-run the same file/choice for real, after reviewing a dry run
  const runForReal = () => runBulkImport(bulkImportLastMoveExisting, false);

  const handleEdit = (unit) => {
    console.log('EditBusinessUnit: Navigating to edit page for unit:', unit._id);
    navigate(`/dashboard/business-units/${unit._id}?mode=edit`);
  };

  // Invitation handling functions
  const copyInvitationLink = async (unit, event) => {
    event.stopPropagation();
    
    if (!unit.invitation || !unit.invitation.token) {
      message.error('No invitation link available');
      return;
    }

    const invitationLink = `${window.location.origin}/register/participant/${unit.invitation.token}`;
    
    try {
      await navigator.clipboard.writeText(invitationLink);
      message.success('Invitation link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = invitationLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('Invitation link copied to clipboard!');
    }
  };

  const generateInvitation = async (unit, event) => {
    event.stopPropagation();
    
    Modal.confirm({
      title: 'Generate Invitation Link',
      content: 'This will create a shareable invitation link for participants to join this organization. Continue?',
      okText: 'Yes, Generate Link',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log('Generating invitation for business unit:', unit._id);
          const result = await businessUnitService.sendParticipantInvitation(unit._id, 'invite@taplearngo.com');
          console.log('Generate invitation result:', result);
          if (result.success) {
            message.success('Invitation link generated successfully!');
            loadBusinessUnits(); // Refresh to get updated invitation data
          } else {
            message.error(result.message || 'Failed to generate invitation link');
          }
        } catch (error) {
          console.error('Error generating invitation:', error);
          message.error('Failed to generate invitation link');
        }
      }
    });
  };

  const formatExpirationDate = (expiresAt) => {
    if (!expiresAt) return 'No expiration';
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffTime < 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Expires today';
    } else if (diffDays <= 3) {
      return `Expires in ${diffDays} days`;
    } else {
      return `Expires ${expiry.toLocaleDateString()}`;
    }
  };

  const isInvitationExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date() > new Date(expiresAt);
  };

  return (
    <>
      <style>{cardStyles}</style>
      <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
        <PageHeader 
          title="Organisations"
          breadcrumbs={['Dashboard', 'Organisations']}
        />
        
        <SearchCard
          searchPlaceholder="Search for organisations by name, industry, country..."
          searchValue={searchText}
          onSearchChange={(e) => setSearchText(e.target.value)}
          filters={[
            {
              label: 'Industry',
              value: selectedIndustry,
              onChange: (value) => setSelectedIndustry(value || ''),
              options: filterOptions.industries
            },
            {
              label: 'Country', 
              value: selectedCountry,
              onChange: (value) => setSelectedCountry(value || ''),
              options: filterOptions.countries
            },
            {
              label: 'Region',
              value: selectedRegion,
              onChange: (value) => setSelectedRegion(value || ''),
              options: filterOptions.regions
            },
            {
              label: 'Managing Group',
              value: selectedGroup,
              onChange: (value) => setSelectedGroup(value || ''),
              options: filterOptions.managingGroups
            }
          ]}
          actionButtons={[
            {
              text: 'Bulk Import',
              icon: <UploadOutlined />,
              type: 'default',
              onClick: () => { setBulkImportResults(null); setBulkImportFile(null); setBulkImportWasDryRun(false); setBulkImportDryRun(true); setBulkImportVisible(true); }
            },
            {
              text: 'Create Organization',
              icon: <PlusOutlined />,
              type: 'primary',
              onClick: () => navigate('/dashboard/business-units/new')
            }
          ]}
        />

        {/* Organizations Grid */}
        <Spin spinning={loading}>
          <Row gutter={[16, 16]}>
            {getFilteredBusinessUnits().map((unit) => (
              <Col xs={24} sm={12} md={8} lg={6} key={unit._id}>
                <Card 
                  className="business-unit-card"
                  onClick={() => navigate(`/dashboard/businessunit/${unit._id}`)}
                  style={{ 
                    height: '100%', 
                    position: 'relative',
                    borderRadius: '8px',
                    border: '1px solid #f0f0f0',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  bodyStyle={{ padding: '16px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(46, 134, 171, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                  }}
                >
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    className="edit-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(unit);
                    }}
                  />
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={5} style={{ marginBottom: '4px', color: '#2E86AB' }}>
                      {unit.name}
                    </Title>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                      {unit.industry || 'Industry'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <GlobalOutlined />
                      {unit.country || 'Country'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <BuildOutlined />
                      {unit.departments?.length || 0} Department{(unit.departments?.length || 0) !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      {unit.questionnaireCount || 0} / {unit.totalQuestionnaires || 121} Quizzes
                    </div>
                    <Progress 
                      percent={unit.questionnaireProgress || 0} 
                      size="small" 
                      strokeColor="#1890ff"
                      showInfo={false}
                    />
                  </div>

                  {/* Invitation Section - Only show if invitation exists */}
                  {unit.invitation && unit.invitation.token && (
                    <div style={{ marginBottom: '16px', padding: '6px', borderLeft: '3px solid #52c41a', paddingLeft: '8px' }}>
                      {!isInvitationExpired(unit.invitation.expiresAt) ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', color: '#666' }}>
                            Invitation expires {formatExpirationDate(unit.invitation.expiresAt)}
                          </span>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => copyInvitationLink(unit, e)}
                            style={{ padding: '2px' }}
                          />
                        </div>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#999' }}>
                          Invitation expired
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {unit.employeeCount || 0} Members
                      </span>
                      <div style={{ display: 'flex', marginLeft: '8px' }}>
                        {unit.topParticipants?.slice(0, 3).map((participant, index) => (
                          <div
                          title='${participant.name}'
                            key={index}
                            className="member-avatar"
                            style={{
                              backgroundColor: ['#1890ff', '#52c41a', '#faad14'][index % 3]
                            }}
                          >
                            {participant.name ? participant.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                        )) || ['U', 'J', 'K'].map((letter, index) => (
                          <div
                            key={index}
                            className="member-avatar"
                            style={{
                              backgroundColor: ['#1890ff', '#52c41a', '#faad14'][index]
                            }}
                          >
                            {letter}
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/businessunit/${unit._id}/members`);
                      }}
                      style={{ padding: '0 4px', fontSize: '12px', color: '#2E86AB' }}
                    >
                      Members
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>
      </div>
      {/* Bulk Import Modal */}
      <Modal
        title="Bulk Import — Organisations, Participants & Groups"
        open={bulkImportVisible}
        onCancel={() => setBulkImportVisible(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setBulkImportVisible(false)}>Close</Button>,
          <Button key="import" type="primary" icon={<UploadOutlined />} loading={bulkImportLoading} disabled={!bulkImportFile} onClick={handleBulkImport}>
            {bulkImportLoading ? 'Running...' : bulkImportDryRun ? 'Preview (Dry Run)' : 'Run Import'}
          </Button>
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="CSV Format"
          description={
            <div>
              <p style={{ margin: '4px 0' }}>Required columns (exact names, case-insensitive):</p>
              <code>name, surname, email, phone, department, organization, group</code>
              <p style={{ margin: '4px 0' }}>At least one of <b>email</b> or <b>phone</b> is required per row.</p>
              <p style={{ margin: '8px 0 4px' }}>What the import does per row:</p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Creates the <b>organization</b> (business unit) if it doesn't exist</li>
                <li>Creates the <b>group</b> and links it to that organization if it doesn't exist</li>
                <li>Adds the <b>department</b> to the organization if missing</li>
                <li>Creates the <b>participant</b> assigned to that org, dept and group — if the email or phone number already exists, you'll be asked whether to update that person's org/dept/group or skip the row</li>
              </ul>
            </div>
          }
        />

        <Dragger
          accept=".csv"
          multiple={false}
          beforeUpload={(file) => { setBulkImportFile(file); return false; }}
          onRemove={() => setBulkImportFile(null)}
          fileList={bulkImportFile ? [bulkImportFile] : []}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Click or drag CSV file here</p>
          <p className="ant-upload-hint">Supports the MBSA export format. Max 10 MB.</p>
        </Dragger>

        <Checkbox
          checked={bulkImportDryRun}
          onChange={(e) => setBulkImportDryRun(e.target.checked)}
          style={{ marginBottom: 16 }}
        >
          Dry run first (preview what would happen — nothing is saved)
        </Checkbox>

        {bulkImportResults && (
          <div>
            {bulkImportWasDryRun && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Dry run — nothing was saved"
                description="This was a preview only. Review the counts below, then run for real when you're ready."
                action={
                  <Button size="small" type="primary" loading={bulkImportLoading} onClick={runForReal}>
                    Run for real
                  </Button>
                }
              />
            )}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <Tag color="green">{bulkImportResults.created?.length || 0} {bulkImportWasDryRun ? 'Would create' : 'Created'}</Tag>
              <Tag color="blue">{bulkImportResults.moved?.length || 0} {bulkImportWasDryRun ? 'Would update' : 'Updated'}</Tag>
              <Tag color="orange">{bulkImportResults.skipped?.length || 0} {bulkImportWasDryRun ? 'Would skip' : 'Skipped'} (duplicate)</Tag>
              <Tag color="red">{bulkImportResults.failed?.length || 0} {bulkImportWasDryRun ? 'Would fail' : 'Failed'}</Tag>
            </div>
            {bulkImportResults.failed?.length > 0 && (
              <Table
                size="small"
                dataSource={bulkImportResults.failed.map((r, i) => ({ ...r, key: i }))}
                columns={[
                  { title: 'Row', dataIndex: 'row', width: 60 },
                  { title: 'Error', dataIndex: 'error' }
                ]}
                pagination={false}
                scroll={{ y: 200 }}
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default BusinessUnits;
